<template>
    <ds-card
        data-qa="plan-overview-card"
        class="plan-overview-card"
    >
        <ds-card-header
            data-qa="card-sub-title"
            class="card-sub-title"
        >
            <h4
                data-qa="plan-overview-header"
                class="plan-overview-header"
            >
                {{ $t('myaccount.quick.add.plan.overview.heading') }}
            </h4>
        </ds-card-header>
        <ds-card-body
            data-qa="card-body"
            class="card-body"
        >
            <table>
                <tr class="line">
                    <td
                        class="line-item"
                    >
                        {{ plan.name }}
                    </td>

                    <td class="line-item-price">
                        {{ getPrice(plan.price) }}
                    </td>
                </tr>
            </table>

            <div
                data-qa="plan-overview"
                class="plan-overview"
            >
                <DsCollapsible
                    id="collapsible"
                    :is-open="isPlanOverviewDetailsExpanded"
                    @toggle="isPlanOverviewDetailsExpanded = !isPlanOverviewDetailsExpanded"
                >
                    <template #header>
                        <div
                            data-qa="collapsible-header"
                            class="collapsible-header"
                        >
                            <p> {{ $t('myaccount.quick.add.details.title') }}</p>

                            <DsIcon :name="isPlanOverviewDetailsExpanded ? 'chevron-up' : 'chevron-down'" class="icon" />
                        </div>
                    </template>

                    <template #default>
                        <table>
                            <tr v-for="(lineItem, index) in includedLineItems" :key="index" class="line">
                                <td
                                    class="line-item"
                                >
                                    <p>{{ lineItem.name }}</p>
                                    <p class="line-item-description">
                                        {{ getQuantityText(lineItem, lineItem.quantity) }}
                                    </p>
                                </td>

                                <td class="line-item-price">
                                    {{ getPrice(lineItem.price) }}
                                </td>
                            </tr>
                        </table>
                    </template>
                </DsCollapsible>
            </div>

            <div
                v-if="displayAddonOverview"
                data-qa="addon-overview"
                class="addon-overview"
            >
                <h4 id="addon-overview-header">
                    {{ $t('myaccount.quick.add.addons.title') }}
                </h4>
                <table>
                    <tr v-if="topLineItem" class="line">
                        <td
                            class="line-item"
                        >
                            <p>{{ topLineItem.name }}</p>
                            <p class="line-item-description">
                                {{ getQuantityText(topLineItem, topLineItemQuantity) }}
                            </p>
                        </td>

                        <td class="line-item-price">
                            {{ getPrice(topLineItemPrice) }}
                        </td>
                    </tr>
                </table>

                <DsCollapsible
                    v-if="additionalLineItems.length"
                    id="collapsible"
                    :is-open="isAddonsDetailsExpanded"
                    @toggle="isAddonsDetailsExpanded = !isAddonsDetailsExpanded"
                >
                    <template #header>
                        <div class="collapsible-header">
                            <p>
                                {{ otherAddonsTitle }}
                            </p>

                            <DsIcon :name="isAddonsDetailsExpanded ? 'chevron-up' : 'chevron-down'" class="icon" />
                        </div>
                    </template>

                    <template #default>
                        <table>
                            <tr v-for="(lineItem, index) in additionalLineItems" :key="index" class="line">
                                <td
                                    class="line-item"
                                >
                                    <p>{{ lineItem.name }}</p>
                                    <p class="line-item-description">
                                        {{ getQuantityText(lineItem) }}
                                    </p>
                                </td>

                                <td class="line-item-price">
                                    {{ getPrice(lineItem.price) }}
                                </td>
                            </tr>
                        </table>
                    </template>
                </DsCollapsible>
            </div>

            <div
                v-if="isVerificationStage || isFinishedStage"
                data-qa="tax-total"
                class="tax-total"
            >
                <table>
                    <tr class="sales-tax-line">
                        <td
                            class="line-item"
                        >
                            {{ $t('myaccount.quick.add.sales.tax.title') }}
                        </td>

                        <td
                            v-if="changeEstimateLoading"
                            data-qa="line-item-price-placeholder"
                            class="line-item-price"
                        >
                            <DsPlaceholder :rows="placeholder" />
                        </td>

                        <td v-else class="line-item-price">
                            {{ getPrice(chaneEstimateNextTermSalesTax) }}
                        </td>
                    </tr>

                    <tr class="total-line">
                        <td
                            class="line-item"
                        >
                            {{ $t('myaccount.quick.add.total.title') }}
                        </td>

                        <td
                            v-if="changeEstimateLoading"
                            data-qa="line-item-price-placeholder"
                            class="line-item-price"
                        >
                            <DsPlaceholder :rows="placeholder" />
                        </td>

                        <td v-else class="line-item-price">
                            {{ getPrice(changeEstimateNextTermTotal) }}
                        </td>
                    </tr>
                </table>
            </div>

            <div
                v-if="isSelectionStage"
                data-qa="total"
                class="total"
            >
                <table v-if="!allowConfirm">
                    <tr class="line">
                        <td
                            class="line-item"
                        >
                            {{ $t('myaccount.quick.add.total.title') }}
                        </td>

                        <td class="line-item-price">
                            {{ getPrice(total) }}
                        </td>
                    </tr>
                </table>

                <table v-else>
                    <tr class="line">
                        <td
                            class="line-item"
                        >
                            {{ $t('myaccount.quick.add.estimated.total.title') }}
                        </td>

                        <td class="line-item-price">
                            {{ getNextTermEstimatedTotalWithPeriod(total) }}
                        </td>
                    </tr>
                </table>

                <DsFilledButton
                    :disabled="!allowConfirm"
                    @click="continueWithSelection"
                >
                    Continue
                </DsFilledButton>
            </div>

            <div
                v-else-if="isVerificationStage || isFinishedStage"
                data-qa="total"
                class="total"
            >
                <table v-if="!isConfirmationSuccess">
                    <tr class="line">
                        <td class="line-item">
                            {{ $t('myaccount.quick.add.todays.charge.title') }}
                        </td>

                        <td v-if="!changeEstimateLoading" class="line-item-price">
                            {{ getPriceNoPeriod(todaysCharge) }}
                        </td>
                        <td
                            v-else
                            data-qa="line-item-price-placeholder"
                            class="line-item-price"
                        >
                            <DsPlaceholder :rows="placeholder" />
                        </td>
                    </tr>
                </table>

                <div
                    v-if="!changeEstimateLoading && !isConfirmationSuccess"
                >
                    <div
                        v-if="reactivationIsRequired"
                        data-qa="reactivation-confirmation"
                        class="reactivation-confirmation"
                    >
                        <DsCheckbox
                            v-model="reactivationConfirmationValue"
                            data-qa="checkbox"
                            class="checkbox"
                            required
                        />

                        <p>
                            {{ $t('myaccount.quick.add.plan.overview.reactivation.confirmation.text', { planName: plan.name, period: formattedPeriod} ) }}
                        </p>
                    </div>
                    <div
                        v-if="removalOfScheduledChangesRequired"
                        data-qa="scheduled-changes-confirmation"
                        class="scheduled-changes-confirmation"
                    >
                        <DsCheckbox
                            v-model="cancelScheduledChangeConfirmationValue"
                            data-qa="checkbox"
                            class="checkbox"
                            required
                        />

                        <p>
                            {{ $t('myaccount.quick.add.plan.overview.scheduled.change.cancellation.confirmation.text') }}
                        </p>
                    </div>
                </div>
                <DsFilledButton
                    v-if="!isConfirmationSuccess"
                    data-qa="confirm-change-button"
                    :disabled="confirmChangeDisabled"
                    :loading="isConfirmationLoading"
                    @click="confirmChange"
                >
                    {{ $t('myaccount.quick.add.change.summary.confirm.charge') }}
                </DsFilledButton>
                <div v-else>
                    <p class="today-charge-sentence">
                        {{ todaysChargeSentence() }}
                    </p>
                    <p class="future-charge-sentence">
                        {{ futureChargeSentence() }}
                    </p>
                </div>
            </div>
            <TermsAndSpamPolicy
                data-qa="terms-and-spam-policy-text"
                class="terms-and-spam-policy-text"
            />
        </ds-card-body>
    </ds-card>
</template>

<script>
import TermsAndSpamPolicy from '../shared/TermsAndSpamPolicy.vue';
import moment from 'moment/moment';

const MONTH = 'MONTH';
const SELECTION_STAGE = 0;
const VERIFY_STAGE = 1;
const FINISHED_STAGE = 2;
const ACTIVE = 'ACTIVE';
const LOADING = 'loading';
const SUCCESS = 'success';

export default {
    components: { TermsAndSpamPolicy },

    props: {
        pricingTiers: {
            type: Array,
            default() {
                return [];
            },
        },
        stage: Number,
        selectedTier: Number,
        plan: Object,
        lineItems: Array,
        currencySymbol: String,
        periodUnit: String,
        quickAddType: String,
        total: Number,
        currentTier: Number,
        changeEstimateLoading: Boolean,
        changeEstimate: Object,
        confirmationLoadingStatus: String,
        subscriptionDetails: Object,
    },

    data() {
        return {
            placeholder: [
                { height: '1rem', boxes: [1] },
            ],
            isPlanOverviewDetailsExpanded: false,
            isAddonsDetailsExpanded: false,
            reactivationConfirmationValue: false,
            cancelScheduledChangeConfirmationValue: false,
        };
    },

    computed: {
        includedLineItems() {
            return this.lineItems?.filter(({ name }) => {
                return name.toLowerCase().includes('included');
            });
        },

        otherAddonsTitle() {
            return this.$tc(
                'myaccount.quick.add.other.addons.title',
                this.additionalLineItems?.length,
                { count: this.additionalLineItems?.length },
            );
        },

        additionalLineItems() {
            return this.lineItems?.filter(({ name, type }) => {
                return !name.toLowerCase().includes('included') && type !== this.quickAddType;
            }) || [];
        },

        topLineItem() {
            const defaultTopLineItem = {
                metadata: null,
                name: 'Additional Contacts',
                price: 0,
                quantity: '0',
                type: this.quickAddType,
            };

            return this.lineItems?.find(({ name, type }) => {
                return name.toLowerCase().includes('additional') && type === this.quickAddType;
            }) || defaultTopLineItem;
        },

        displayAddonOverview() {
            return this.additionalLineItems?.length !== 0 || this.topLineItem !== null;
        },

        isSelectionStage() {
            return this.stage === SELECTION_STAGE;
        },

        isVerificationStage() {
            return this.stage === VERIFY_STAGE;
        },

        isFinishedStage() {
            return this.stage === FINISHED_STAGE;
        },

        allowConfirm() {
            return this.currentTier < this.selectedTier;
        },

        todaysCharge() {
            return this.changeEstimate?.proratedEstimate?.total;
        },

        changeEstimateNextTermTotal() {
            return this.changeEstimate?.nextTermEstimate?.total;
        },

        chaneEstimateNextTermSalesTax() {
            return this.changeEstimate?.nextTermEstimate?.taxAmount;
        },

        nextTermTotal() {
            return this.subscriptionDetails?.estimate?.total;
        },

        topLineItemQuantity() {
            if (this.currentTier === this.selectedTier) {
                return this.topLineItem.quantity;
            }

            return this.pricingTiers.at(this.selectedTier)?.maxUnits;
        },

        topLineItemPrice() {
            if (this.currentTier === this.selectedTier) {
                return this.topLineItem.price;
            }

            return this.pricingTiers.at(this.selectedTier)?.unitPrice;
        },

        isActive() {
            return this.changeEstimate.subscriptionStatus === ACTIVE;
        },

        confirmChangeDisabled() {
            return this.changeEstimateLoading || !this.requiredCheckboxesAreChecked;
        },

        requiredCheckboxesAreChecked() {
            let requiredCheckboxesAreChecked = false;

            if (this.reactivationIsRequired && this.removalOfScheduledChangesRequired) {
                requiredCheckboxesAreChecked = this.reactivationConfirmationValue && this.cancelScheduledChangeConfirmationValue;
            } else if (this.reactivationIsRequired) {
                requiredCheckboxesAreChecked = this.reactivationConfirmationValue;
            } else if (this.removalOfScheduledChangesRequired) {
                requiredCheckboxesAreChecked = this.cancelScheduledChangeConfirmationValue;
            } else {
                requiredCheckboxesAreChecked = true;
            }

            return requiredCheckboxesAreChecked;
        },

        reactivationIsRequired() {
            return !this.isActive;
        },

        removalOfScheduledChangesRequired() {
            return this.subscriptionDetails.hasScheduledChanges;
        },

        formattedPeriod() {
            if (this.periodUnit === 'YEAR') {
                return this.$t('myaccount.quick.add.yearly');
            }

            return this.$t('myaccount.quick.add.monthly');
        },

        isConfirmationLoading() {
            return this.confirmationLoadingStatus === LOADING;
        },

        isConfirmationSuccess() {
            return this.confirmationLoadingStatus === SUCCESS;
        },
    },

    methods: {
        getPrice(price) {
            const endingText = this.$t(this.periodUnit === MONTH ? 'myaccount.quick.add.monthlyEnding' : 'myaccount.quick.add.yearlyEnding');

            return `${this.currencySymbol}${this.numberWithCommas(this.numberWithFixedDecimal(price))}${endingText}`;
        },

        getPriceNoPeriod(price) {
            return `${this.currencySymbol}${this.numberWithCommas(this.numberWithFixedDecimal(price))}`;
        },

        getNextTermEstimatedTotalWithPeriod(price) {
            const endingText = this.$t(this.periodUnit === MONTH ? 'myaccount.quick.add.monthlyEnding' : 'myaccount.quick.add.yearlyEnding');

            return `${this.getNextTermEstimatedTotal(price)}${endingText}`;
        },

        getNextTermEstimatedTotal(price) {
            let estimatedPrice = 0;

            // Current tier is set to -1 by default. This value is overwritten if a customer has
            // this addon purchased prior to getting to this modal.
            if (this.currentTier === -1) {
                estimatedPrice = price + this.pricingTiers.at(this.selectedTier)?.unitPrice;
            } else {
                const priceBeforeChange = this.pricingTiers.at(this.currentTier)?.unitPrice;
                const priceAfterChange = this.pricingTiers.at(this.selectedTier)?.unitPrice;

                estimatedPrice = price + (priceAfterChange - priceBeforeChange);
            }

            return `${this.currencySymbol}${this.numberWithCommas(this.numberWithFixedDecimal(estimatedPrice))}`;
        },

        numberWithCommas(x) {
            if (!x) {
                return 0;
            }
            const parts = x.toString().split('.');

            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

            return parts.join('.');
        },

        numberWithFixedDecimal(x) {
            if (!x) {
                return '0';
            }

            return Number.parseFloat(x).toFixed(2);
        },

        getQuantityText(lineItem, lineItemQuantity) {
            let description = '';

            switch (lineItem.type) {
            case 'CONTACT': {
                const quantityAsNumber = Number(lineItemQuantity);

                description = this.$tc(
                    'myaccount.quick.add.contacts',
                    quantityAsNumber,
                    { count: this.numberWithCommas(quantityAsNumber) },
                );
                break;
            }

            case 'SMS': {
                const quantityAsNumber = Number(lineItemQuantity);

                description = this.$tc(
                    'myaccount.quick.add.sms',
                    quantityAsNumber,
                    { count: this.numberWithCommas(quantityAsNumber) },
                );
                break;
            }

            case 'USER': {
                const quantityAsNumber = Number(lineItemQuantity);

                description = this.$tc(
                    'myaccount.quick.add.users',
                    quantityAsNumber,
                    { count: this.numberWithCommas(quantityAsNumber) },
                );
                break;
            }
            case 'COUPON':
                break;

            case 'MINUTES_AND_SMS_TIER': {
                const metadata = JSON.parse(lineItem.metadata);
                let sms = '';

                let minutes = '';

                metadata.forEach((limit) => {
                    if (limit.addonType.toLowerCase().includes('sms')) {
                        const limitAsNumber = Number(limit.limit);

                        sms = this.$tc(
                            'myaccount.quick.add.sms',
                            limitAsNumber,
                            { count: this.numberWithCommas(limitAsNumber) },
                        );
                    } else if (limit.addonType.toLowerCase().includes('minutes')) {
                        const limitAsNumber = Number(limit.limit);

                        minutes = this.$tc(
                            'myaccount.quick.add.minutes',
                            limitAsNumber,
                            { count: this.numberWithCommas(limitAsNumber) },
                        );
                    }
                });

                description = `${sms} and ${minutes}`;
                break;
            }

            case 'LOCAL_PHONE_NUMBER':
            case 'SMS_BUNDLE':
            default:
                break;
            }

            return description;
        },

        continueWithSelection() {
            this.$emit('continue-with-selection');
        },

        confirmChange() {
            this.$emit('change-confirmed');
        },

        dateFormatDate(date, format = 'll') {
            const formattedDate = moment.utc(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        todaysChargeSentence() {
            return this.$t('myaccount.quick.add.confirmation.charge.today', { amount: this.getPriceNoPeriod(this.todaysCharge) });
        },

        futureChargeSentence() {
            return this.$t('myaccount.quick.add.confirmation.future.charge', {
                period: this.formattedBillingFrequency, amount: this.getNextTermEstimatedTotal(this.nextTermTotal), date: this.dateFormatDate(this.subscriptionDetails.nextBillingAt),
            });
        },

        formattedBillingFrequency() {
            if (this.subscriptionDetails?.billingFrequency == null) {
                return null;
            }

            if (this.subscriptionDetails?.billingFrequency === 'MONTH') {
                return 'monthly';
            }

            return 'yearly';
        },
    },
};

</script>

<style lang="scss" scoped>
.plan-overview-card {
    width: 288px;

    .card-sub-title {
        .plan-overview-header {
            color: $color-gray-800;
            line-height: $line-height-lg;
            padding: $spacing-200 $spacing-200 0;
            font-weight: 500;
            font-size: $font-size-md;
        }
    }

    .card-body  {
        display: grid;

        padding-top: 0;

        .total {
            display: contents;

            .reactivation-confirmation {
                display: flex;

                .checkbox {
                    padding: 0;
                    align-items: flex-start;
                }
            }

            .scheduled-changes-confirmation {
                display: flex;

                .checkbox {
                    padding: 0;
                    align-items: flex-start;
                }
            }

            .continue-button {
                width: 100%;
            }
        }

        .tax-total {
            border-bottom: 1px solid $color-gray-300;
        }

        .line {
            align-self: stretch;
            align-items: center;
            display: flex;
            justify-content: space-between;
            border: none;
            line-height: $line-height-lg;
            padding: $spacing-100 0;
        }

        .sales-tax-line {
            align-self: stretch;
            align-items: center;
            display: flex;
            justify-content: space-between;
            border: none;
            line-height: $line-height-lg;
            padding: 0.5rem 0;
        }

        .total-line {
            align-self: stretch;
            align-items: center;
            display: flex;
            justify-content: space-between;
            border: none;
            line-height: $line-height-lg;
            padding: 0 0 0.5rem 0;
        }

        tr {
            border: none;
        }

        #addon-overview-header {
            color: $color-gray-800;
            line-height: $line-height-lg;
            padding-top: $spacing-200;
            font-weight: 500;
            font-size: $font-size-md;
        }

        tr td {
            width: fit-content;
        }

        tr td.line-item-price {
            font-weight: $font-weight-regular;
            text-align: right;
            padding: 0;
            font-family: $font-table-number;
            word-break: inherit;
            min-width: 4.75rem;
        }

        tr td.line-item {
            color: $color-ink;
            font-style: normal;
            font-weight: 500;
            padding: 0;
            font-size: $font-size-sm;
            word-break: break-word;
            p {
                margin: 0;
            }
            .line-item-description {
                font-size: $font-size-xs;
                font-weight: $font-weight-regular;
            }
        }

        .collapsible-header {
            display: flex;
            --icon-color: #{$color-blue-700};
            --icon-size: #{$icon-size-small};
            p {
                font-size: $font-size-xs;
                margin: 0;
                align-self: center;
            }

            .icon {
                align-self: center;
            }
        }

        .plan-overview {
            padding-bottom: $spacing-200;
            border-bottom: 1px solid $color-gray-300;
        }

        .addon-overview {
            padding-bottom: $spacing-200;
            border-bottom: 1px solid $color-gray-300;
        }

        ::v-deep #collapsible .collapsible-content .collapsible-inner {
            padding: 0;
            margin: 0;
        }

        .terms-and-spam-policy-text {
            justify-self: center;
            padding-top: $spacing-200;
            margin: 0;
        }

        .today-charge-sentence {
            margin-top: $spacing-200;
            font-size: $font-size-sm;
            font-weight: 500;
            text-align: center;
        }

        .future-charge-sentence {
            font-size: $font-size-xs;
            font-weight: 400;
            text-align: center;
            margin: 0;
        }
    }
}

</style>
